
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ProductRecommendationCard } from '@/components/packing/ProductRecommendationCard';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { ReadingProgressBar } from '@/components/ui/reading-progress-bar';
import { Skeleton } from '@/components/ui/skeleton';

export default function ProductsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  // Fetch products from both tables
  const { data: products, isLoading, isFetching } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: async () => {
      // Fetch products from product_recommendations table
      const { data: recommendationsData, error: recommendationsError } = await supabase
        .from('product_recommendations')
        .select('*, packing_lists(title, destination_type)')
        .order('name');
        
      if (recommendationsError) throw recommendationsError;
      
      // Transform recommendations data
      const recommendationProducts = recommendationsData.map(product => ({
        ...product,
        source: 'recommendations' as const,
        packing_list_title: product.packing_lists?.title || '',
        packing_list_destination: product.packing_lists?.destination_type || '',
        created_at: product.created_at || new Date().toISOString()
      }));
      
      // Fetch products from products table
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .order('name');
        
      if (productsError) throw productsError;
      
      // Transform products data
      const permanentProducts = productsData.map(product => ({
        ...product,
        source: 'products' as const,
        created_at: product.created_at || new Date().toISOString()
      }));
      
      // Combine both sets of products
      const allProducts = [...recommendationProducts, ...permanentProducts];
      
      // Remove duplicates using the same logic as in ProductGrid.tsx
      return removeDuplicates(allProducts);
    }
  });
  
  // Extract all unique categories
  const categories = products 
    ? ['all', ...new Set(products.map(product => product.category).filter(Boolean))]
    : ['all'];
  
  // Filter products based on search query and selected category
  const filteredProducts = products?.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase()));
      
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Show loading indicator
  const isPageLoading = isLoading || isFetching;
  
  // Function to remove duplicate products
  // Uses the same logic from ProductGrid.tsx
  function removeDuplicates(products) {
    // Create a map to track products by a unique combination of name and affiliate link
    const productMap = new Map();
    
    // Process products in reverse order to prioritize 'products' over 'recommendations'
    // when we have both versions of the same product
    [...products].reverse().forEach(product => {
      // Create a unique key from name + affiliate link (normalized)
      const key = `${product.name.toLowerCase()}-${product.affiliate_link.toLowerCase()}`;
      
      // When adding to the map, products from 'products' table will overwrite
      // duplicates from 'recommendations' table due to the reverse order
      productMap.set(key, product);
    });
    
    // Convert the map values back to an array
    const uniqueProducts = Array.from(productMap.values());
    
    // Sort the products to match original order
    return uniqueProducts.sort((a, b) => {
      // First by source ('products' first, then 'recommendations')
      if (a.source !== b.source) {
        return a.source === 'products' ? -1 : 1;
      }
      
      // Then by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }
  
  return (
    <div className="container mb-16">
      <ReadingProgressBar isLoading={isPageLoading} />
      
      <Helmet>
        <title>Travel Gear & Products | Trotting the World</title>
        <meta 
          name="description" 
          content="Discover the best travel gear, equipment, and products recommended by experienced travelers. Find the perfect items for your next adventure." 
        />
      </Helmet>
      
      <header className="mb-10 text-center">
        <h1 className="text-4xl font-bold tracking-tight mb-4">
          Recommended Travel Products
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Discover the best gear and equipment to make your travels more comfortable and enjoyable.
        </p>
      </header>
      
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <div className="flex-shrink-0">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="flex flex-wrap overflow-hidden">
              {categories.map(category => (
                <TabsTrigger key={category} value={category} className="capitalize">
                  {category === 'all' ? 'All Categories' : category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-lg overflow-hidden">
              <Skeleton className="h-48 w-full" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-10 w-full rounded-md mt-2" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredProducts && filteredProducts.length > 0 ? (
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 transition-opacity duration-300 ${isFetching ? 'opacity-70' : 'opacity-100'}`}>
          {filteredProducts.map((product, index) => (
            <ProductRecommendationCard 
              key={`${product.source}-${product.id}-${index}`} 
              product={product} 
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-lg mb-4">No products match your search criteria.</p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
            }}
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}
